import { useCallback, useEffect, useMemo, useState } from 'react';
import { callEdgeFunction, supabase } from './supabaseClient';
import './AnalyticsPage.css';

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
const fmtPct = (n, total) => {
  if (!total || total <= 0 || n == null) return null;
  return `${Math.round((n / total) * 100)}%`;
};
const fmtMoney = (n, { withTl = false } = {}) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const value = Number(n).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return withTl ? `${value} TL` : value;
};
const fmtDay = (ymd) => {
  if (!ymd) return '';
  const [, m, d] = ymd.split('-');
  return `${d}.${m}`;
};
const shortId = (id) => {
  if (!id) return '—';
  return `${String(id).slice(0, 8)}…`;
};

function Panel({ title, meta, children, className = '' }) {
  return (
    <section className={`ax-panel ${className}`.trim()}>
      {(title || meta) ? (
        <header className="ax-panel-head">
          {title ? <h2 className="ax-panel-title">{title}</h2> : null}
          {meta ? <p className="ax-panel-meta">{meta}</p> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

function MetricRow({ label, value, sub }) {
  return (
    <div className="ax-row">
      <span className="ax-row-label">{label}</span>
      <div className="ax-row-end">
        {sub ? <span className="ax-row-sub">{sub}</span> : null}
        <span className="ax-row-value">{value}</span>
      </div>
    </div>
  );
}

function StatusBar({ segments }) {
  const total = segments.reduce((s, seg) => s + (Number(seg.value) || 0), 0);
  if (!total) {
    return <div className="ax-status-bar ax-status-bar--empty" aria-hidden />;
  }
  return (
    <div className="ax-status-bar" role="img" aria-label="QR durum dağılımı">
      {segments.map((seg) => {
        const pct = ((Number(seg.value) || 0) / total) * 100;
        if (pct <= 0) return null;
        return (
          <span
            key={seg.key}
            className={`ax-status-seg ax-status-seg--${seg.key}`}
            style={{ width: `${pct}%` }}
            title={`${seg.label}: ${fmtNum(seg.value)}`}
          />
        );
      })}
    </div>
  );
}

function Overview({ data, total, qr, loading, embedded = false }) {
  if (loading) {
    return (
      <div className={`ax-overview ax-overview--loading${embedded ? ' ax-overview--embedded' : ''}`} aria-busy="true">
        <div className="ax-spotlight-shimmer" />
        <div className="ax-pulse-shimmer" />
      </div>
    );
  }

  const confirmedPct = fmtPct(data?.email_confirmed, total);
  const saveRate = qr?.original_sum > 0
    ? Math.round((Number(qr.discount_sum || 0) / Number(qr.original_sum)) * 100)
    : null;

  return (
    <div className={`ax-overview${embedded ? ' ax-overview--embedded' : ''}`}>
      <div className="ax-spotlight" aria-label="Öne çıkan metrikler">
        <article className="ax-spot ax-spot--users">
          <div className="ax-spot-top">
            <p className="ax-spot-label">Kullanıcı</p>
            <p className="ax-spot-value">{fmtNum(total)}</p>
          </div>
          <p className="ax-spot-meta">
            {confirmedPct ? `${confirmedPct} onaylı` : '—'}
            {data?.staff_total != null ? ` · ${fmtNum(data.staff_total)} ekip` : ''}
          </p>
        </article>
        <article className="ax-spot ax-spot--money">
          <div className="ax-spot-top">
            <p className="ax-spot-label">İndirim</p>
            <p className="ax-spot-value">{fmtMoney(qr?.discount_sum, { withTl: true })}</p>
          </div>
          <p className="ax-spot-meta">
            {qr?.with_discount != null ? `${fmtNum(qr.with_discount)} QR` : '—'}
            {saveRate != null ? ` · %${saveRate}` : ''}
          </p>
        </article>
      </div>

      <div className="ax-pulse" aria-label="Hareket">
        <div className="ax-pulse-item">
          <strong>{fmtNum(data?.new_7d)}</strong>
          <span>Yeni 7g</span>
        </div>
        <div className="ax-pulse-item">
          <strong>{fmtNum(data?.active_7d)}</strong>
          <span>Aktif 7g</span>
        </div>
        <div className="ax-pulse-item">
          <strong>{fmtNum(qr?.used)}</strong>
          <span>Kullanılan</span>
        </div>
        <div className="ax-pulse-item">
          <strong>{fmtNum(qr?.active)}</strong>
          <span>Aktif QR</span>
        </div>
      </div>
    </div>
  );
}

function UsersDetailPanel({ data, total, loading }) {
  if (loading) {
    return (
      <Panel title="Kullanıcı detayı">
        <div className="ax-summary-shimmer-stack" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ax-summary-shimmer ax-summary-shimmer--row" />
          ))}
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Kullanıcı detayı" meta="Dönem kırılımları">
      <div className="ax-summary-rows">
        <MetricRow label="Yeni · 24 saat" value={fmtNum(data?.new_24h)} sub={fmtPct(data?.new_24h, total)} />
        <MetricRow label="Yeni · 30 gün" value={fmtNum(data?.new_30d)} sub={fmtPct(data?.new_30d, total)} />
        <MetricRow label="Aktif · 24 saat" value={fmtNum(data?.active_24h)} sub={fmtPct(data?.active_24h, total)} />
        <MetricRow label="Aktif · 30 gün" value={fmtNum(data?.active_30d)} sub={fmtPct(data?.active_30d, total)} />
        <MetricRow label="Onaylı e-posta" value={fmtNum(data?.email_confirmed)} sub={fmtPct(data?.email_confirmed, total)} />
        <MetricRow label="İndirimli hesap" value={fmtNum(data?.with_discount)} sub={fmtPct(data?.with_discount, total)} />
      </div>
    </Panel>
  );
}

function QrDetailPanel({ qr, loading, compact = false }) {
  if (loading) {
    return (
      <Panel title="QR durumu">
        <div className="ax-summary-shimmer-stack" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ax-summary-shimmer ax-summary-shimmer--row" />
          ))}
        </div>
      </Panel>
    );
  }

  const segments = [
    { key: 'used', label: 'Kullanılan', value: qr?.used || 0 },
    { key: 'active', label: 'Aktif', value: qr?.active || 0 },
    { key: 'expired', label: 'Süresi dolmuş', value: qr?.expired || 0 },
  ];
  const saveRate = qr?.original_sum > 0
    ? Math.round((Number(qr.discount_sum || 0) / Number(qr.original_sum)) * 100)
    : null;

  return (
    <Panel
      title="QR durumu"
      meta={qr?.total != null ? `${fmtNum(qr.total)} kayıt` : 'Simpra QR'}
      className="ax-panel--qr"
    >
      <div className="ax-qr-status">
        <StatusBar segments={segments} />
        <div className="ax-qr-status-legend">
          {segments.map((seg) => (
            <span key={seg.key} className={`ax-qr-legend ax-qr-legend--${seg.key}`}>
              <i />
              {seg.label}
              <strong>{fmtNum(seg.value)}</strong>
            </span>
          ))}
        </div>
      </div>

      <div className="ax-summary-rows">
        {!compact ? (
          <MetricRow
            label="Verilen indirim"
            value={fmtMoney(qr?.discount_sum, { withTl: true })}
            sub={saveRate != null ? `%${saveRate}` : null}
          />
        ) : null}
        <MetricRow label="Ödenen" value={fmtMoney(qr?.paid_sum, { withTl: true })} />
        <MetricRow label="Orijinal" value={fmtMoney(qr?.original_sum, { withTl: true })} />
        <MetricRow label="İndirimli QR" value={fmtNum(qr?.with_discount)} />
      </div>
    </Panel>
  );
}

function SignupsChart({ series }) {
  const height = 132;
  const max = useMemo(() => series.reduce((m, p) => Math.max(m, p.c), 0) || 1, [series]);
  const totalSignups = useMemo(() => series.reduce((s, p) => s + (Number(p.c) || 0), 0), [series]);
  const barWidth = series.length ? 100 / series.length : 100;
  const peak = useMemo(() => {
    let best = null;
    for (const p of series) {
      if (!best || p.c > best.c) best = p;
    }
    return best;
  }, [series]);

  if (!series.length) return null;

  return (
    <Panel title="Günlük yeni kayıt" meta={`Son 30 gün · ${fmtNum(totalSignups)} kayıt`}>
      <div className="ax-chart-stats">
        <span>
          Zirve <strong>{fmtNum(peak?.c)}</strong>
          {peak?.d ? <span className="ax-chart-stats-dim"> · {fmtDay(peak.d)}</span> : null}
        </span>
        <span>
          Ort. <strong>{fmtNum(Math.round(totalSignups / series.length))}</strong>
          <span className="ax-chart-stats-dim"> / gün</span>
        </span>
      </div>
      <svg
        className="ax-chart-svg"
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Günlük yeni kayıt grafiği"
      >
        <defs>
          <linearGradient id="ax-signups-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6e7468" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#6e7468" stopOpacity="0.28" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((t) => {
          const y = height - 12 - t * (height - 28);
          return (
            <line
              key={t}
              x1="0"
              x2="100"
              y1={y}
              y2={y}
              stroke="#e2e8f0"
              strokeWidth="0.25"
              strokeDasharray="1.2 1.2"
            />
          );
        })}
        {series.map((p, i) => {
          const h = (p.c / max) * (height - 28);
          const x = i * barWidth;
          const y = height - 12 - h;
          return (
            <rect
              key={p.d}
              x={x + barWidth * 0.18}
              y={y}
              width={barWidth * 0.64}
              height={Math.max(h, p.c > 0 ? 1.2 : 0.35)}
              rx={1.1}
              fill="url(#ax-signups-grad)"
              opacity={p.c === 0 ? 0.18 : 1}
            >
              <title>{`${p.d}: ${p.c}`}</title>
            </rect>
          );
        })}
        <line x1="0" x2="100" y1={height - 12} y2={height - 12} stroke="#e2e8f0" strokeWidth="0.35" />
      </svg>
      <div className="ax-chart-axis">
        <span>{fmtDay(series[0]?.d)}</span>
        <span>{fmtDay(series[Math.floor(series.length / 2)]?.d)}</span>
        <span>{fmtDay(series[series.length - 1]?.d)}</span>
      </div>
    </Panel>
  );
}

function TopDiscountsPanel({ rows }) {
  if (!rows?.length) return null;

  return (
    <Panel title="En çok atanan indirimler" meta="Kullanıcı metadata">
      <ul className="ax-top-list">
        {rows.slice(0, 6).map((row) => {
          const amount = row.amount != null ? Number(row.amount) : null;
          const type = row.type || '';
          const amountLabel = Number.isFinite(amount)
            ? (type === 'amount' ? fmtMoney(amount) : `%${fmtNum(amount)}`)
            : null;
          return (
            <li key={row.discount_id || `disc-${row.count}`} className="ax-top-item">
              <div className="ax-top-main">
                <code className="ax-top-id" title={row.discount_id}>{shortId(row.discount_id)}</code>
                {amountLabel ? <span className="ax-top-amt">{amountLabel}</span> : null}
              </div>
              <span className="ax-top-count">{fmtNum(row.count)}</span>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function ProvidersPanel({ byProvider }) {
  const entries = useMemo(() => {
    if (!byProvider || typeof byProvider !== 'object') return [];
    return Object.entries(byProvider)
      .map(([provider, count]) => ({ provider, count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count);
  }, [byProvider]);

  if (!entries.length) return null;

  const max = entries[0]?.count || 1;

  return (
    <Panel title="Giriş sağlayıcıları" meta="Auth identities">
      <ul className="ax-provider-list">
        {entries.map(({ provider, count }) => (
          <li key={provider} className="ax-provider-item">
            <div className="ax-provider-row">
              <span className="ax-provider-name">{provider}</span>
              <span className="ax-provider-count">{fmtNum(count)}</span>
            </div>
            <div className="ax-provider-bar" aria-hidden>
              <span style={{ width: `${Math.max(6, (count / max) * 100)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

async function fetchQrStats() {
  const pageSize = 1000;
  let from = 0;
  const stats = {
    total: 0,
    used: 0,
    active: 0,
    expired: 0,
    with_discount: 0,
    discount_sum: 0,
    paid_sum: 0,
    original_sum: 0,
  };

  for (;;) {
    const { data, error } = await supabase
      .from('qr_codes')
      .select('status, discount_amount, total_amount, original_amount')
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const batch = data || [];
    for (const row of batch) {
      stats.total += 1;
      if (row.status === 'used') stats.used += 1;
      else if (row.status === 'active') stats.active += 1;
      else if (row.status === 'expired') stats.expired += 1;

      const discount = Number(row.discount_amount);
      if (Number.isFinite(discount) && discount > 0) {
        stats.with_discount += 1;
        stats.discount_sum += discount;
      }
      const paid = Number(row.total_amount);
      if (Number.isFinite(paid)) stats.paid_sum += paid;
      const original = Number(row.original_amount);
      if (Number.isFinite(original)) stats.original_sum += original;
    }
    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return stats;
}

export default function AnalyticsPage({ embedded = false }) {
  const [data, setData] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [payload, qrStats] = await Promise.all([
        callEdgeFunction('admin-user-analytics', { method: 'POST' }),
        fetchQrStats().catch(() => null),
      ]);
      let parsed = payload;
      if (typeof payload === 'string') {
        try { parsed = JSON.parse(payload); } catch { parsed = {}; }
      }
      setData(parsed || {});
      setQr(qrStats);
    } catch (e) {
      setError(e?.message || 'Analitik yüklenemedi.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data?.total_users || 0;
  const signups = Array.isArray(data?.signups_daily_30) ? data.signups_daily_30 : [];
  const topDiscounts = Array.isArray(data?.top_discounts) ? data.top_discounts : [];
  const generatedLabel = data?.generated_at
    ? new Date(data.generated_at).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    : null;

  return (
    <div className={`analytics-page${embedded ? ' analytics-page--embedded' : ' admin-page-content'}`}>
      <header className={`analytics-header${embedded ? ' analytics-header--embedded' : ''}`}>
        <div>
          <p className="analytics-kicker">{embedded ? 'Özet' : 'Yönetim'}</p>
          <h2 className="analytics-title">Analitik</h2>
          <p className="analytics-sub">
            {embedded
              ? (generatedLabel ? `Güncellendi ${generatedLabel}` : 'Kullanıcı ve QR özeti')
              : `Kullanıcı büyümesi, QR kullanımı ve verilen indirimler${generatedLabel ? ` · ${generatedLabel}` : ''}`}
          </p>
        </div>
        <button type="button" className="analytics-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : 'Yenile'}
        </button>
      </header>

      {error ? <p className="analytics-error" role="alert">{error}</p> : null}

      <Overview data={data} total={total} qr={qr} loading={loading} embedded={embedded} />

      <div className={`ax-layout${embedded ? ' ax-layout--embedded' : ''}`}>
        <div className="ax-layout-main">
          {!loading && signups.length > 0 ? <SignupsChart series={signups} /> : null}
          {embedded ? (
            <QrDetailPanel qr={qr} loading={loading} compact />
          ) : (
            <>
              <div className="ax-layout-split">
                <TopDiscountsPanel rows={topDiscounts} />
                <ProvidersPanel byProvider={data?.by_provider} />
              </div>
            </>
          )}
        </div>
        {embedded ? null : (
          <aside className="ax-layout-side">
            <QrDetailPanel qr={qr} loading={loading} />
            <UsersDetailPanel data={data} total={total} loading={loading} />
          </aside>
        )}
      </div>
    </div>
  );
}
